import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation, Trans } from "i18n";
import { AxiosError } from "axios";
import usePagePermission from "app/hooks/usePagePermission";
import { useReturnUrl } from "app/hooks/useReturnUrl";
import { checkIsCD, checkIsPerformer, useAuth } from "shared-auth";
import {
  PhoneFieldFormik,
  TextInputFormik,
  SelectCountryFormik,
  SelectStateProvinceFormik,
  Button,
  Spinner,
  COUNTRIES_WITH_STATE_PROVINCE,
  QuestionTooltip,
  generateBreadcrumbsData,
  Head,
  CommonErrorPopup,
} from "component-library";
import { dataTestIds } from "data-testids";
import PageWrapperWithinLayout from "app-shell/PageWrapperWithinLayout";
import useGetAddressDetail from "./hooks/useGetAddressDetail";
import { IAddress, updateAddress } from "services/api/BillingApi/BillingApi";
import { FieldNames } from "pages/AccountSettings/MyDetails/constants";
import { validationSchema } from "./validationSchema";
import { BASE_PATH, ACCOUNT_SETTINGS_FULL_PATH } from "config/common";
import { breadcrumbs, BreadcrumbSection } from "app/constants";
import "./styles.scss";

const defaultInitialValues = {
  [FieldNames.city]: "",
  [FieldNames.country]: "",
  [FieldNames.region]: "",
  [FieldNames.street1]: "",
  [FieldNames.street2]: "",
  [FieldNames.postalCode]: "",
  [FieldNames.phone]: "",
};

interface RecurlyErrorParam {
  param: string;
  message?: string;
}

interface RecurlyErrorData {
  params?: RecurlyErrorParam[];
  code?: string;
  message?: string;
  classification?: string;
}

interface ApiErrorResponse {
  RecurlyError?: RecurlyErrorData;
}

const MyDetails = () => {
  const { user } = useAuth();
  const isCD = checkIsCD();
  usePagePermission([checkIsPerformer, isCD], BASE_PATH);
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<IAddress>>(null);
  const { t } = useTranslation();
  const { data, isLoading } = useGetAddressDetail();
  const initialValues = { ...defaultInitialValues, ...data?.address };
  const userName = user?.profile.name;
  const { getReturnUrl, redirectToReturnUrl } = useReturnUrl();
  const [error, setError] = useState<{
    isError: boolean;
    errorStatusCode: number | undefined;
  }>({
    isError: false,
    errorStatusCode: undefined,
  });

  const goToMyAccount = (hasAddressChanged: boolean) => {
    if (getReturnUrl() && hasAddressChanged) {
      redirectToReturnUrl();
    } else {
      navigate(ACCOUNT_SETTINGS_FULL_PATH, { replace: true });
    }
  };

  const submit = async (values: IAddress) => {
    try {
      setError({
        isError: false,
        errorStatusCode: undefined,
      });
      await updateAddress({
        ...values,
        stateProvince: values.region,
      });
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      const recurlyError = axiosError?.response?.data?.RecurlyError;

      if (recurlyError?.params?.length) {
        let hasFieldErrors = false;

        // Map RecurlyError params to form field errors
        recurlyError.params.forEach(({ param }) => {
          if (param === "address.postal_code") {
            formRef?.current?.setFieldError(
              FieldNames.postalCode,
              t("common:validation.notValid", {
                field: t("common:label.zipPostCodeColon"),
              })
            );
            hasFieldErrors = true;
          }
          // Add more field mappings here as needed
        });

        // If we handled validation errors on the form, don't show popup
        if (hasFieldErrors) {
          return;
        }
      }

      // Show popup for all other errors (axios errors, non-validation RecurlyErrors, etc.)
      setError({
        isError: true,
        errorStatusCode: axiosError?.response?.status,
      });

      return;
    }

    if (isCD) return navigate(BASE_PATH);

    goToMyAccount(true);
  };

  const handleCloseErrorPopup = () =>
    setError({
      isError: false,
      errorStatusCode: undefined,
    });

  if (isLoading) return <Spinner />;

  return (
    <>
      {error.isError && (
        <CommonErrorPopup
          errorStatusCode={error.errorStatusCode}
          popupProps={{ close: handleCloseErrorPopup }}
          alertMessageProps={{
            buttons: [
              {
                name: t("common:button.label.close"),
                click: handleCloseErrorPopup,
                type: "primary",
              },
            ],
          }}
        />
      )}
      <Head>
        <title>{t("performer:accountSettings.myDetails.header")}</title>
      </Head>
      <PageWrapperWithinLayout
        pageHeader={t("performer:accountSettings.myDetails.header")}
        subFooter={{
          text: t("common:button.label.close"),
          onClick: () => (isCD ? navigate(BASE_PATH) : goToMyAccount(false)),
        }}
        breadcrumbs={generateBreadcrumbsData(
          breadcrumbs,
          isCD
            ? [BreadcrumbSection.MyAccount]
            : [BreadcrumbSection.MyAccount, BreadcrumbSection.AccountSettings]
        )}
      >
        <Formik<IAddress>
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          onSubmit={submit}
          validateOnBlur={false}
        >
          {({ values, submitForm, isSubmitting }) => {
            return (
              <div className="my-details-page py-5">
                <div className="pb-5">
                  <Trans
                    i18nKey="performer:accountSettings.myDetails.pageDescription"
                    components={[
                      <br key="br" />,
                      <b key="b" />,
                      <a
                        href="https://support.spotlight.com/hc/en-us"
                        target="_blank"
                        rel="noreferrer"
                        key="a"
                      />,
                    ]}
                  />
                </div>
                <Form>
                  <div className="mb-4 border-b-[1px] border-grey-light-2">
                    <div className="max-w-[440px]">
                      <div className="mb-5 flex justify-between border-b-[1px] border-grey-light-2 pb-5">
                        <div className="font-semibold">
                          {t("common:label.name")}
                        </div>
                        <div className="mr-20">{userName}</div>
                      </div>
                      <div>
                        <div className="section-title">
                          {t(
                            "performer:accountSettings.myDetails.provideYourPhone"
                          )}
                        </div>
                        <PhoneFieldFormik
                          name={FieldNames.phone}
                          label={t("common:label.mobileNumberColon")}
                          tooltipComponent={() => (
                            <QuestionTooltip
                              title={t(
                                "performer:accountSettings.myDetails.tooltip.mobileNumberHeader"
                              )}
                              content={t(
                                "performer:accountSettings.myDetails.tooltip.mobileNumberBody"
                              )}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="max-w-[440px]">
                    <div>
                      <div className="section-title">
                        {t(
                          "performer:accountSettings.myDetails.provideYourAddress"
                        )}
                      </div>
                      <TextInputFormik
                        name={FieldNames.street1}
                        label={t("common:label.address1Colon")}
                        annex={
                          <QuestionTooltip
                            title={t(
                              "performer:accountSettings.myDetails.tooltip.address1Header"
                            )}
                            content={t(
                              "performer:accountSettings.myDetails.tooltip.address1Body"
                            )}
                          />
                        }
                      />
                      <TextInputFormik
                        name={FieldNames.street2}
                        label={t("common:label.address2OptionalColon")}
                      />
                      <TextInputFormik
                        name={FieldNames.city}
                        label={t("common:label.cityColon")}
                      />
                      <TextInputFormik
                        name={FieldNames.postalCode}
                        label={t("common:label.zipPostCodeColon")}
                      />
                      <SelectCountryFormik
                        name={FieldNames.country}
                        label={t("common:label.countryColon")}
                        placeholder={t("common:placeholder.selectCountry")}
                        searchableBySearchbox
                      />
                      {COUNTRIES_WITH_STATE_PROVINCE.includes(
                        values.country
                      ) && (
                        <SelectStateProvinceFormik
                          name={FieldNames.region}
                          selectedCountryCode={values.country}
                          label={t("common:label.stateProvinceColon")}
                          placeholder={t(
                            "common:placeholder.selectStateProvince"
                          )}
                        />
                      )}
                    </div>
                  </div>
                </Form>
                <div className="flex justify-end">
                  <Button
                    type="primary"
                    text={t("common:button.label.updateDetails")}
                    className="max-w-[330px]"
                    onClick={submitForm}
                    loading={isSubmitting}
                    data-testid={
                      dataTestIds.userAccountApp[
                        "AccountSettings.MyDetails.MyDetails.updateDetailsButton"
                      ]
                    }
                  />
                </div>
              </div>
            );
          }}
        </Formik>
      </PageWrapperWithinLayout>
    </>
  );
};

export default MyDetails;
